import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Table, TableHeaderRow, TableHeaderCell, TableRow, TableCell, Dialog, Bar, Input, Label, Select, Option, CheckBox, MessageStrip, FlexBox, BusyIndicator, ObjectStatus, Text } from '@ui5/webcomponents-react';
import type { AppUserRow } from '@b1links/core';
import { api } from '../../api/client';
import { PageHeader } from '../../components/Responsive';

const CHANNELS = ['web', 'mobile', 'desktop'] as const;

/** App-user admin (UDT @APPUSER): create/edit, assign role, allowed channels, reset password, deactivate. */
export function UsersAdmin() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const users = useQuery({ queryKey: ['admin-users'], queryFn: api.adminUsers });
  const roles = useQuery({ queryKey: ['admin-roles'], queryFn: api.adminRoles });
  const [edit, setEdit] = useState<AppUserRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState<Record<string, string>>({});
  const [chans, setChans] = useState<Set<string>>(new Set());
  const [pwd, setPwd] = useState('');
  const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);

  const open = (u?: AppUserRow) => {
    setErr(null); setPwd(''); setCreating(!u); setEdit(u ?? { Code: '', Name: '' });
    setF({ Code: u?.Code ?? '', Name: u?.Name ?? '', U_Role: u?.U_Role ?? '', U_Email: u?.U_Email ?? '', U_SapUser: u?.U_SapUser ?? '', U_Active: u?.U_Active ?? 'Y' });
    setChans(new Set((u?.U_Channels ?? 'web,mobile').split(',').map(s => s.trim()).filter(Boolean)));
  };
  const close = () => setEdit(null);
  const set = (k: string, v: string) => setF(x => ({ ...x, [k]: v }));

  const save = async () => {
    if (!f.Code || !f.Name) { setErr('Code and name are required'); return; }
    if (creating && pwd.length < 6) { setErr('Password (min 6) required for a new user'); return; }
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = { Code: f.Code, Name: f.Name, U_Role: f.U_Role || undefined, U_Email: f.U_Email || undefined, U_SapUser: f.U_SapUser || undefined, U_Active: f.U_Active, U_Channels: [...chans].join(',') };
      if (pwd) body.password = pwd;
      if (creating) await api.createUser(body); else await api.updateUser(f.Code, body);
      await qc.invalidateQueries({ queryKey: ['admin-users'] }); close();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const deactivate = async (u: AppUserRow) => { if (!window.confirm(`Deactivate ${u.Code}?`)) return; try { await api.deactivateUser(u.Code); qc.invalidateQueries({ queryKey: ['admin-users'] }); } catch (e) { alert((e as Error).message); } };

  if (users.isLoading) return <FlexBox justifyContent="Center" style={{ padding: '3rem' }}><BusyIndicator active /></FlexBox>;

  return (
    <div>
      <PageHeader title="App Users" style={{ marginBottom: '1rem' }} actions={<Button design="Emphasized" icon="add" onClick={() => open()}>New user</Button>} />
      <MessageStrip design="Information" hideCloseButton style={{ marginBottom: '0.5rem' }}>
        These are B1Links app users (SAP UDT @APPUSER) used by the web and mobile channels. Desktop uses real SAP Business One users.
      </MessageStrip>
      <Table overflowMode="Popin" headerRow={<TableHeaderRow>
        <TableHeaderCell minWidth="6rem" importance={2}><span>Code</span></TableHeaderCell><TableHeaderCell minWidth="9rem"><span>Name</span></TableHeaderCell><TableHeaderCell minWidth="6rem"><span>Role</span></TableHeaderCell>
        <TableHeaderCell minWidth="8rem"><span>Channels</span></TableHeaderCell><TableHeaderCell minWidth="6rem"><span>Status</span></TableHeaderCell><TableHeaderCell minWidth="6rem" importance={1}><span></span></TableHeaderCell>
      </TableHeaderRow>}>
        {(users.data ?? []).map(u => (
          <TableRow key={u.Code}>
            <TableCell><span>{u.Code}</span></TableCell><TableCell><span>{u.Name}</span></TableCell><TableCell><span>{u.U_Role}</span></TableCell>
            <TableCell><span>{u.U_Channels}</span></TableCell>
            <TableCell><ObjectStatus state={u.U_Active === 'Y' ? 'Positive' : 'Negative'}>{u.U_Active === 'Y' ? 'Active' : 'Inactive'}</ObjectStatus></TableCell>
            <TableCell><FlexBox style={{ gap: '0.25rem' }}><Button icon="edit" design="Transparent" tooltip="Edit" onClick={() => open(u)} /><Button icon="locked" design="Transparent" tooltip="Screen access" onClick={() => nav(`/admin/access?kind=user&code=${encodeURIComponent(u.Code)}`)} /><Button icon="decline" tooltip="Deactivate" design="Transparent" onClick={() => deactivate(u)} /></FlexBox></TableCell>
          </TableRow>
        ))}
      </Table>

      {edit && (
        <Dialog open headerText={creating ? 'New user' : `Edit ${edit.Code}`} style={{ width: 'min(560px, 96vw)' }}
          footer={<Bar endContent={<><Button onClick={close}>Cancel</Button><Button design="Emphasized" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button></>} />}>
          {err && <MessageStrip design="Negative" onClose={() => setErr(null)}>{err}</MessageStrip>}
          <FlexBox direction="Column" style={{ gap: '0.6rem', padding: '0.5rem 0' }}>
            <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>
              <div><Label>Code</Label><Input value={f.Code} disabled={!creating} onInput={e => set('Code', e.target.value ?? '')} /></div>
              <div style={{ flex: 1, minWidth: '10rem' }}><Label>Name</Label><Input value={f.Name} style={{ width: '100%' }} onInput={e => set('Name', e.target.value ?? '')} /></div>
            </FlexBox>
            <div><Label>Role</Label>
              <Select style={{ width: '100%' }} onChange={e => set('U_Role', e.detail.selectedOption.dataset.v ?? '')}>
                <Option data-v="" selected={!f.U_Role}>(none)</Option>
                {(roles.data ?? []).map(r => <Option key={r.Code} data-v={r.Code} selected={f.U_Role === r.Code}>{r.Name}</Option>)}
              </Select>
            </div>
            <div><Label>Email</Label><Input value={f.U_Email} style={{ width: '100%' }} onInput={e => set('U_Email', e.target.value ?? '')} /></div>
            <div><Label>Linked SAP user (optional, for switching this user to SAP auth)</Label><Input value={f.U_SapUser} style={{ width: '100%' }} onInput={e => set('U_SapUser', e.target.value ?? '')} /></div>
            <div><Label>Allowed channels</Label>
              <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>{CHANNELS.map(c => <FlexBox key={c} alignItems="Center" style={{ gap: '0.3rem' }}><CheckBox checked={chans.has(c)} onChange={e => setChans(s => { const n = new Set(s); e.target.checked ? n.add(c) : n.delete(c); return n; })} /><Text>{c}</Text></FlexBox>)}</FlexBox>
            </div>
            <div><Label>{creating ? 'Password' : 'Reset password (leave blank to keep)'}</Label><Input type="Password" value={pwd} style={{ width: '100%' }} onInput={e => setPwd(e.target.value ?? '')} /></div>
            {!creating && <FlexBox alignItems="Center" style={{ gap: '0.5rem' }}><CheckBox checked={f.U_Active === 'Y'} onChange={e => set('U_Active', e.target.checked ? 'Y' : 'N')} /><Text>Active</Text></FlexBox>}
          </FlexBox>
        </Dialog>
      )}
    </div>
  );
}
